import { useEffect, useState } from 'react';
import './style.css'
import {
  CustomBarChart,
  CustomAreaChart,
  CustomLineChart,
  CustomPieChart,
  CustomDonutChart,
  CustomScatterPlot,
  CustomGeoMap,
  CustomHorizontalBarChart,
  CustomMultiHorizontalBarChart,
  CustomMultiBarChart,
  CustomMultiBarChartWithLine,
  CustomBarChartWithLine,
  CustomMultiLineChartWithSecondaryAxis
} from '../ui/custom-chart';
import { ExpandIcon } from '../Icons/iconImages';
import { Button } from 'antd';

type ChartType = 'bar-chart' | 'multi-bar' | 'multi_bar_chart' | 'line-graph' | 'area-chart' | 'pie-chart' | 'donut-chart' | 'scatter-plot' | 'geo-map' | 'horizontal-bar' | 'multi-horizontal-bar' | 'multi-bar-chart-secondary-y-axis' | 'bar-graph-secondary-y-axis' | 'line-graph-secondary-y-axis';
type ChartSize = 'small' | 'medium' | 'large';

interface ChartDataPoint {
  [key: string]: string | number;
}

interface BarChartProps {
  data: ChartDataPoint[];
  type: ChartType;
  size?: ChartSize;
  dataXkey?: string;
  dataYKey?: string | string[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  className?: string;
  fill?: string;
  isResponsive?: boolean;
  groupKey?: string;
  dualAxisThreshold?: number | false;
  showExpandButton?: boolean;
  onExpand?: () => void;
}

function CustomChart({
  data,
  type,
  size = 'medium',
  dataXkey,
  dataYKey,
  xAxisLabel,
  yAxisLabel,
  fill = '#279964',
  className,
  isResponsive = true,
  groupKey,
  dualAxisThreshold,
  showExpandButton = false,
  onExpand,
}: BarChartProps) {
  const [visibleDataCount, setVisibleDataCount] = useState(data?.length ?? 0);

  useEffect(() => {
    setVisibleDataCount(data?.length ?? 0);
  }, [data]);

  // Early return for empty data
  if (!data || data.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center h-64 text-gray-400">
          No data available
        </div>
      </div>
    );
  }

  const chartSizeConfig = {
    pie: {
      responsive: {
        small: { height: 200, barWidth: 20 },
        medium: { height: 250, barWidth: 32 },
        large: { height: 300, barWidth: 40 },
      },
      standard: {
        small: { height: 280, barWidth: 20 },
        medium: { height: 350, barWidth: 32 },
        large: { height: 400, barWidth: 40 },
      }
    },
    regular: {
      small: { height: 370, barWidth: 20 },
      medium: { height: 400, barWidth: 32 },
      large: { height: 500, barWidth: 40 },
    }
  };

  const getChartSize = () => {
    const isPieChart = type === 'pie-chart' || type === 'donut-chart';

    if (isPieChart) {
      const sizeSet = isResponsive ? chartSizeConfig.pie.responsive : chartSizeConfig.pie.standard;
      return sizeSet[size] || sizeSet.medium;
    }

    return chartSizeConfig.regular[size] || chartSizeConfig.regular.medium;
  };

  const { height, barWidth } = getChartSize();

  // Determine if we have multiple data keys
  const isMultiBar = Array.isArray(dataYKey) && dataYKey.length > 1;

  // Get the right data key(s)
  const dataKeyProp = Array.isArray(dataYKey) ? dataYKey[0] : dataYKey;

  // For pie charts, ensure we have proper key detection
  // If no dataYKey is provided, let the pie chart auto-detect
  // Always extract the first key if it's an array
  const pieDataKey: string | undefined = type === 'pie-chart' || type === 'donut-chart'
    ? (Array.isArray(dataYKey) ? dataYKey[0] : (Array.isArray(dataKeyProp) ? dataKeyProp[0] : dataKeyProp))
    : (Array.isArray(dataKeyProp) ? dataKeyProp[0] : dataKeyProp);

  // Render chart content based on type
  const renderChart = () => {
    if (type === 'multi-bar' || type === 'multi_bar_chart') {
      return (
        <CustomMultiBarChart
          data={data}
          dataKey={Array.isArray(dataYKey) ? undefined : dataKeyProp}
          xAxisKey={dataXkey}
          className={className}
          height={height}
          barWidth={barWidth}
          fill={fill}
          showGrid={true}
          showTooltip={true}
          showLegend={true}
          dualAxisThreshold={dualAxisThreshold}
        />
      );
    }

    if (type === 'multi-bar-chart-secondary-y-axis') {
      return (
        <CustomMultiBarChartWithLine
          data={data}
          dataKey={Array.isArray(dataYKey) ? undefined : dataKeyProp}
          xAxisKey={dataXkey}
          className={className}
          height={height}
          barWidth={barWidth}
          fill={fill}
          showGrid={true}
          showTooltip={true}
          showLegend={true}
        />
      );
    }

    if (type === 'bar-graph-secondary-y-axis') {
      return (
        <CustomBarChartWithLine
          data={data}
          dataKey={Array.isArray(dataYKey) ? undefined : dataKeyProp}
          xAxisKey={dataXkey}
          className={className}
          height={height}
          barWidth={barWidth}
          fill={fill}
          showGrid={true}
          showTooltip={true}
          showLegend={true}
        />
      );
    }

    if (type === 'line-graph-secondary-y-axis') {
      return (
        <CustomMultiLineChartWithSecondaryAxis
          data={data}
          dataKey={Array.isArray(dataYKey) ? undefined : dataKeyProp}
          xAxisKey={dataXkey}
          className={className}
          height={height}
          fill={fill}
          showGrid={true}
          showTooltip={true}
          showLegend={true}
        />
      );
    }

    if (type === 'bar-chart') {
      return (
        <CustomBarChart
          data={data}
          dataKey={dataKeyProp}
          xAxisKey={dataXkey}
          className={className}
          height={height}
          barWidth={barWidth}
          fill={fill}
          multiBar={isMultiBar}
          showGrid={true}
          showTooltip={true}
          showLegend={isMultiBar}
        />
      );
    }

    if (type === 'line-graph') {
      return (
        <CustomAreaChart
          data={data}
          dataKey={isMultiBar ? undefined : dataKeyProp}
          xAxisKey={dataXkey}
          className={className}
          height={height}
          fill={fill}
          multiBar={isMultiBar}
          showGrid={true}
          showTooltip={true}
          showLegend={isMultiBar}
          strokeWidth={2}
          fillOpacity={0.2}
          smooth={true}
        />
      );
    }

    if (type === 'area-chart') {
      return (
        <CustomAreaChart
          data={data}
          dataKey={isMultiBar ? undefined : dataKeyProp}
          xAxisKey={dataXkey}
          className={className}
          height={height}
          fill={fill}
          multiBar={isMultiBar}
          showGrid={true}
          showTooltip={true}
          showLegend={isMultiBar}
          strokeWidth={2}
          fillOpacity={0.8}
          smooth={true}
        />
      );
    }

    if (type === 'pie-chart') {
      return (
        <CustomPieChart
          data={data}
          dataKey={pieDataKey}
          labelKey={dataXkey}
          className={className}
          size={height}
          showTooltip={true}
          showLegend={true}
          showLabels={false}
        />
      );
    }

    if (type === 'donut-chart') {
      return (
        <CustomDonutChart
          data={data}
          dataKey={pieDataKey}
          labelKey={dataXkey}
          className={className}
          size={height}
          showTooltip={true}
          showLegend={true}
          showLabels={false}
          innerRadius={50}
        />
      );
    }

    if (type === 'scatter-plot') {
      return (
        <CustomScatterPlot
          data={data}
          xKey={dataXkey}
          yKey={dataKeyProp}
          className={className}
          height={height}
          fill={fill}
          showGrid={true}
          showTooltip={true}
          showLegend={true}
          groupKey={groupKey}
          dotSize={5}
          xAxisLabel={xAxisLabel}
          yAxisLabel={yAxisLabel}
          isResponsive={isResponsive}
        />
      );
    }

    if (type === 'geo-map') {
      // GeoMap requires data with 'country' and 'value' properties
      // Type assertion is safe here as geo-map data should have these properties
      return (
        <CustomGeoMap
          data={data as Array<{ country: string; value: number;[key: string]: any }>}
          className={className}
          height={height}
          showTooltip={true}
        />
      );
    }

    if (type === 'horizontal-bar') {
      return (
        <CustomHorizontalBarChart
          data={data}
          dataKey={dataKeyProp}
          xAxisKey={dataXkey}
          className={className}
          height={height}
          fill={fill}
          multiBar={isMultiBar}
          showGrid={true}
          showTooltip={true}
          showLegend={isMultiBar}
          barHeight={barWidth}
        />
      );
    }

    if (type === 'multi-horizontal-bar') {
      return (
        <CustomMultiHorizontalBarChart
          data={data}
          dataKey={Array.isArray(dataYKey) ? dataYKey : [dataYKey || '']}
          xAxisKey={dataXkey}
          className={className}
          height={height}
          barHeight={barWidth}
        />
      );
    }

    return null;
  };

  // Wrap chart with expand button if needed
  const chartContent = renderChart();

  if (!showExpandButton || !onExpand) {
    return chartContent;
  }

  return (
    <div className="relative w-full overflow-hidden" style={{ position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 10,
        }}
      >
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onExpand();
          }}
          type="default"
          shape="circle"
          // size="middle"
          className="bg-gray-100 opacity-50 hover:opacity-100"
          icon={<ExpandIcon className="w-4 h-4" />}
          title="Expand chart"
        />
      </div>
      <div className="relative w-full">
        {chartContent}
      </div>
    </div>
  );
}

export default CustomChart;